import React, { useState, useEffect } from 'react';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useAuth } from '../contexts/AuthContext';
import { PostJSON } from '../utils/request';

interface ProfileFormValues {
  firstName: string;
  lastName: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Settings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Profile form
  const profileForm = useForm<ProfileFormValues>({
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    },
    validate: {
      firstName: (value) => (value.trim().length < 1 ? 'First name is required' : null),
      lastName: (value) => (value.trim().length < 1 ? 'Last name is required' : null)
    }
  });

  // Password form
  const passwordForm = useForm<PasswordFormValues>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validate: {
      currentPassword: (value) => (value.length < 1 ? 'Current password is required' : null),
      newPassword: (value) => {
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])/.test(value)) return 'Password must contain at least one lowercase letter';
        if (!/(?=.*[A-Z])/.test(value)) return 'Password must contain at least one uppercase letter';
        if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
        if (!/(?=.*[!@#$%^&*()_+{}:;<>,.?~\\-])/.test(value)) return 'Password must contain at least one special character';
        return null;
      },
      confirmPassword: (value, values) =>
        value !== values.newPassword ? 'Passwords do not match' : null
    }
  });

  // Update form values when user data changes
  useEffect(() => {
    if (user) {
      profileForm.setValues({
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      });
    }
  // profileForm will be updated at every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleProfileUpdate = async (values: ProfileFormValues) => {
    setIsUpdatingProfile(true);
    setProfileError(null);

    try {
      const response = await PostJSON<{ success: boolean; user: { id: number; email: string; firstName?: string; lastName?: string; accountId: number; createdAt: Date; updatedAt: Date } }>('/api/account/update_profile', {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim()
      });

      if (response.success) {
        await refreshUser();
        notifications.show({
          title: 'Success',
          message: 'Profile updated successfully!',
          color: 'green'
        });
        console.log('Profile updated successfully!');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setProfileError(errorMessage);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordUpdate = async (values: PasswordFormValues) => {
    setIsUpdatingPassword(true);
    setPasswordError(null);

    try {
      const response = await PostJSON<{ success: boolean }>('/api/account/update_password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      if (response.success) {
        passwordForm.reset();
        notifications.show({
          title: 'Success',
          message: 'Password updated successfully!',
          color: 'green'
        });
      } else {
        throw new Error('Failed to update password');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update password';
      setPasswordError(errorMessage);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <Stack gap="xl">
      <div>
        <Title mb="xs" order={1}>User Settings</Title>
        <Text c="dimmed">Manage your user settings and preferences</Text>
      </div>

      {/* Profile Information Section */}
      <Paper p="xl" radius="md" shadow="sm">
        <Stack gap="md">
          <div>
            <Title mb="xs" order={3}>Profile Information</Title>
            <Text c="dimmed" size="sm">Update your personal information</Text>
          </div>

          <form onSubmit={profileForm.onSubmit(handleProfileUpdate)}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  required
                  label="First Name"
                  placeholder="Enter your first name"
                  {...profileForm.getInputProps('firstName')}
                />
                <TextInput
                  required
                  label="Last Name"
                  placeholder="Enter your last name"
                  {...profileForm.getInputProps('lastName')}
                />
              </Group>

              {profileError && (
                <Alert
                  color="red"
                  icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                  title="Error"
                  variant="light"
                >
                  {profileError}
                </Alert>
              )}

              <Group justify="flex-end">
                <Button
                  disabled={isUpdatingProfile}
                  loading={isUpdatingProfile}
                  type="submit"
                >
                  {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>

      {/* Password Section */}
      <Paper p="xl" radius="md" shadow="sm">
        <Stack gap="md">
          <div>
            <Title mb="xs" order={3}>Change Password</Title>
            <Text c="dimmed" size="sm">Update your account password</Text>
          </div>

          <form onSubmit={passwordForm.onSubmit(handlePasswordUpdate)}>
            <Stack gap="md">
              <PasswordInput
                required
                label="Current Password"
                placeholder="Enter your current password"
                {...passwordForm.getInputProps('currentPassword')}
              />

              <PasswordInput
                required
                label="New Password"
                placeholder="Enter your new password"
                {...passwordForm.getInputProps('newPassword')}
              />

              <PasswordInput
                required
                label="Confirm New Password"
                placeholder="Confirm your new password"
                {...passwordForm.getInputProps('confirmPassword')}
              />

              {passwordError && (
                <Alert
                  color="red"
                  icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                  title="Error"
                  variant="light"
                >
                  {passwordError}
                </Alert>
              )}

              <Group justify="flex-end">
                <Button
                  disabled={isUpdatingPassword}
                  loading={isUpdatingPassword}
                  type="submit"
                >
                  {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Settings;
